import { FormEvent, KeyboardEvent, MouseEvent } from 'react'
import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import { AlmanacFileIndexItem } from 'hooks/queries/files/types'
import { FavoriteButton } from 'common/FavoriteButton/FavoriteButton'
import { FileType } from 'types'
import { getResolvedLabel } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'

import Styles from './TitleCell.module.scss'
import { TargetIcons } from './TargetIcons'
import { useAuth } from 'hooks/UseAuth'

interface TitleCellProps {
  item: AlmanacFileIndexItem
  isRenaming: boolean
  draftTitle: string
  onRename: (newTitle: string) => void
  onStartRename: () => void
  onDraftTitleChange: (title: string) => void
  onRenameSubmit: (event?: FormEvent<HTMLFormElement>) => void
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  onOpenPreview: () => void
}

const renderFavorite = (item: AlmanacFileIndexItem) => {
  if (item) {
    return (
      <FavoriteButton
        className={cx(Styles.favoriteButton, Styles.favoriteToggle)}
        favoritableId={item.key}
        favoritableType={item.asset_type?.toLowerCase() as FileType}
        favoritableTitle={item.title}
        size="sm"
        tooltipPlacement="bottom"
      />
    )
  }

  return null
}

export const TitleCell = ({
  item,
  isRenaming,
  draftTitle,
  onStartRename,
  onDraftTitleChange,
  onRenameSubmit,
  onKeyDown,
  onOpenPreview,
}: TitleCellProps) => {
  const auth = useAuth()
  const handleTitleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onOpenPreview()
  }

  return (
    <div className={cx(Styles.cell, Styles.titleCell)} role="gridcell">
      <div className={Styles.iconContainer}>
        <TargetIcons item={item} size={15} />
        {getResolvedLabel(item)}
      </div>
      <div className={cx(Styles.titleContent, { [Styles.titleContentRenaming]: isRenaming })}>
        {isRenaming ? (
          <form className={Styles.renameForm} onSubmit={onRenameSubmit}>
            <input
              autoFocus
              value={draftTitle}
              onChange={(event) => onDraftTitleChange(event.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => onRenameSubmit()}
              onClick={(event) => event.stopPropagation()}
              className={Styles.renameInput}
            />
          </form>
        ) : (
          <div className={Styles.titleContentContainer}>
            <div className={Styles.titleContentWrapper}>
              <button
                type="button"
                className={Styles.titleButton}
                onClick={handleTitleClick}
                onDoubleClick={() => onStartRename()}
                title={item.title}
              >
                <span className={Styles.titleText}>
                  <Text lineClamp={1}>{item.title}</Text>
                </span>
                {auth && renderFavorite(item)}
              </button>
            </div>
            <div className={Styles.contextText}>{item.content}</div>
          </div>
        )}
      </div>
    </div>
  )
}
